/* eslint-disable prettier/prettier */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import { CustomerRoles } from '../constants/customerRoles'
import CallService from '../services/callService'

export const CALL_CARD_PRODUCTS = 'CALL_CARD_PRODUCTS'
export const CALL_CARD_CHECKOUT = 'CALL_CARD_CHECKOUT'
export const CALL_CARD_CART = 'CALL_CARD_CART'
export const CALL_CARD_PRODUCT_DETAIL = 'CALL_CARD_PRODUCT_DETAIL'
export const CALL_CARD_CHECKOUT_SUCCESS = 'CALL_CARD_CHECKOUT_SUCCESS'
export const CALL_CARD_CASH_CHECKOUT_SUCCESS = 'CALL_CARD_CASH_CHECKOUT_SUCCESS'
export const CALL_CARD_CASH_CHECKOUT_FAILURE = 'CALL_CARD_CASH_CHECKOUT_FAILURE'
export const CALL_CARD_CHECKOUT_FAILURE = 'CALL_CARD_CHECKOUT_FAILURE'
export const CALL_ACTION_CHAT = 'CALL_ACTION_CHAT'
export const CALL_ACTION_PRODUCTS = 'CALL_ACTION_PRODUCTS'
export const CALL_ACTION_CAMERAS = 'CALL_ACTION_CAMERAS'

export const notifyLikeMeesage = createAsyncThunk(
  'ui/notifyLikeMeesage',
  async (_, { dispatch }) => {
    const messageSent = await CallService.notifyChannelLikeMessage()

    if (messageSent) {
      dispatch(setRemoteLikeAnimation({ remoteLikeAnimation: true }))
    }
  },
)

export const setLikeMessageReceivedHandler = createAsyncThunk(
  'ui/likeMessageReceivedHandler',
  async (messageHandler) => {
    await CallService.setLikeMessageReceivedHandler(messageHandler)
  },
)

export const setExternalReactionReceivedHandler = createAsyncThunk(
  'ui/externalReactionReceivedHandler',
  async (messageHandler) => {
    await CallService.setExternalReactionReceived(messageHandler)
  },
)
export const setQuickAddReceivedHandler = createAsyncThunk(
  'ui/quickAddReceivedHandler',
  async (messageHandler) => {
    await CallService.setQuickAddReceived(messageHandler)
  },
)

export const setChatCooldownMessageHandler = createAsyncThunk(
  'ui/setChatCooldownMessageHandler',
  async (messageHandler) => {
    await CallService.setChatCooldownMessageHandler(messageHandler)
  },
)

export const setEventFinishedMessageHandler = createAsyncThunk(
  'ui/setEventFinishedMessageHandler',
  async (messageHandler) => {
    await CallService.setEventFinishedMessageHandler(messageHandler)
  },
)

export const setDisplayPopUpMessageHandler = createAsyncThunk(
  'ui/setDisplayPopUpMessageHandler',
  async (messageHandler) => {
    await CallService.setDisplayPopUpMessageHandler(messageHandler)
  },
)

export const setChatMessageReceivedHandler = createAsyncThunk(
  'chat/messageReceivedHandler',
  async (messageHandler) => {
    await CallService.setChatMessageReceivedHandler(messageHandler)
  },
)

export const handleAutoPlayUnmute = createAsyncThunk(
  'ui/handleAutoPlayUnmute',
  async (_, { getState }) => {
    const state = getState()
    const { playerRef } = state.ui.autoPlayUnlocker

    if (playerRef) {
      return { shouldUnmute: true }
    }
    return { shouldUnmute: false }
  },
)

export const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    messages: [],
    lang: 'es',
    isOneToMany: false,
    notificationMessage: [],
    isNotificationVisible: false,
    redirectDialog: {
      orderData: null,
      showDialog: false,
      paymentGateway: null,
      externalUrl: null,
      title: '',
    },
    chat: {
      quotedMessage: null,
      pinnedMessage: null,
      cooldown: 0,
      disabledByCooldown: false,
      autoJoin: true,
    },
    showAskNameDialog: {
      open: false,
      allowCancel: true,
      title: null,
      fromScheduled: false,
    },
    autoPlayUnlocker: {
      open: false,
      requestUnmute: false,
    },
    tencentConnectionDialog: {
      open: false,
      dismissed: false,
      errorCode: null,
      errorMessage: null,
    },
    externalReaction: null,
    quickAdd: false,
    remoteLikeAnimation: false,
    localLikeAnimation: false,
    isLikeButtonVisible: false,
    activeCallCard: '',
    activeCallAction: CALL_ACTION_CHAT,
    videoDimensions: {
      width: null,
      height: null,
      aspectRatio: null,
    },
    carousel: {
      currentRow: 0,
    },
    cart: {
      showCheckoutIframe: false,
    },
    call: {
      onCloseMessage: false,
    },
    chatError: { showDialog: false, errorCode: null, errorMessage: null, errorTitle: null },
    payPalDialogVisible: false,
    payPalButtonMounted: false,
    cannotDeleteMessage: {
      allowDelete: true,
    },
    changeMainVideo: true,
    scrollPosition: 0,
    stockError: false,
    postalCodeErrorMessage: '',
    showDraggable: false,
    productTour: {
      isActive: false,
      currentStep: 0,
      completed: false,
    },
    audioPermissionGranted: false,
    isChatRegistered: false,
  },
  reducers: {
    setAutoJoinChat: (state, action) => {
      state.chat.autoJoin = action.payload
    },
    setIsNotificationVisible: (state, action) => {
      state.isNotificationVisible = action.payload.isNotificationVisible
    },
    setNotificationMessage: (state, action) => {
      state.notificationMessage = action.payload.notificationMessage
    },
    setQuotedMessage: (state, action) => {
      state.chat.quotedMessage = action.payload.quotedMessage
    },
    setChatCooldown: (state, action) => {
      state.chat.cooldown = action.payload
    },
    setDisabledByCooldown: (state, action) => {
      state.chat.disabledByCooldown = action.payload
    },
    clearQuotedMessage: (state) => {
      state.chat.quotedMessage = null
    },
    setPinnedMessage: (state, action) => {
      state.chat.pinnedMessage = action.payload
    },
    clearPinnedMessage: (state) => {
      state.chat.pinnedMessage = null
    },
    setRemoteLikeAnimation: (state, action) => {
      state.remoteLikeAnimation = action.payload.remoteLikeAnimation
    },
    setLocalLikeAnimation: (state, action) => {
      state.localLikeAnimation = action.payload.localLikeAnimation
    },
    setIsOneToMany: (state, action) => {
      state.isOneToMany = action.payload.isOneToMany
      state.isLikeButtonVisible = action.payload.isOneToMany
      state.activeCallAction = action.payload.isMobile
        ? CALL_ACTION_PRODUCTS
        : action.payload.isOneToMany
        ? CALL_ACTION_CHAT
        : CALL_ACTION_CAMERAS
    },
    setActiveCallCard: (state, action) => {
      state.activeCallCard = action.payload
    },
    setAudioPermissionGranted: (state, action) => {
      state.audioPermissionGranted = action.payload
    },
    setIsChatRegistered: (state, action) => {
      state.isChatRegistered = action.payload
    },
    closeCallCard: (state) => {
      state.activeCallCard = ''
    },
    goToProducts: (state) => {
      state.activeCallCard = CALL_CARD_PRODUCTS
    },
    setActiveCallAction: (state, action) => {
      state.activeCallAction = action.payload
    },
    setVideoDimensions: (state, action) => {
      state.videoDimensions.width = action.payload.width
      state.videoDimensions.height = action.payload.height
      state.videoDimensions.aspectRatio = action.payload.aspectRatio
    },
    setCurrenRowOnCarousel: (state, action) => {
      state.carousel.currentRow = action.payload.currentRow
    },
    showCheckoutIframe: (state) => {
      state.cart.showCheckoutIframe = true
    },
    hideCheckoutIframe: (state) => {
      state.cart.showCheckoutIframe = false
    },
    showOnCloseMessageCall: (state) => {
      state.call.onCloseMessage = true
    },
    hideOnCloseMessageCall: (state) => {
      state.call.onCloseMessage = false
    },
    setPayPalButtonMounted: (state, action) => {
      state.payPalButtonMounted = action.payload
    },
    setShowAskNameDialog: (state, action) => {
      const { open, allowCancel = null, title, fromScheduled } = action.payload
      state.showAskNameDialog.open = open

      if (allowCancel !== null) {
        state.showAskNameDialog.allowCancel = allowCancel
      }

      if (title !== undefined) {
        state.showAskNameDialog.title = title
      }

      if (fromScheduled !== undefined) {
        state.showAskNameDialog.fromScheduled = fromScheduled
      }
    },
    setLanguage: (state, action) => {
      state.lang = action.payload
    },
    setExternalReaction: (state, action) => {
      state.externalReaction = action.payload.externalReaction
    },
    setQuickAdd(state, action) {
      state.quickAdd = action.payload.quickAdd
    },
    setAutoPlayUnlocker(state, action) {
      if (action.payload.open !== undefined) {
        state.autoPlayUnlocker.open = action.payload.open
      }
      if (action.payload.requestUnmute !== undefined) {
        state.autoPlayUnlocker.requestUnmute = action.payload.requestUnmute
      }
    },
    clearAutoPlayUnmuteRequest(state) {
      state.autoPlayUnlocker.requestUnmute = false
      state.autoPlayUnlocker.open = false
    },
    setTencentConnectionDialog(state, action) {
      const { open, errorCode = null, errorMessage = null } = action.payload

      if (open === true && state.tencentConnectionDialog.dismissed) {
        return
      }

      state.tencentConnectionDialog.open = open
      state.tencentConnectionDialog.errorCode = errorCode
      state.tencentConnectionDialog.errorMessage = errorMessage
    },
    clearTencentConnectionDialog(state) {
      state.tencentConnectionDialog.open = false
      state.tencentConnectionDialog.dismissed = true
      state.tencentConnectionDialog.errorCode = null
      state.tencentConnectionDialog.errorMessage = null
    },
    setRedirectDialog: (state, action) => {
      const {
        orderData = null,
        showDialog,
        paymentGateway,
        externalUrl = null,
        title = '',
      } = action.payload
      state.redirectDialog.paymentGateway = paymentGateway
      state.redirectDialog.orderData = orderData
      state.redirectDialog.showDialog = showDialog

      if (title !== '') {
        state.redirectDialog.title = title
      }
      if (externalUrl !== null) {
        state.redirectDialog.externalUrl = externalUrl
      }
    },
    resetRedirectDialog: (state) => {
      state.redirectDialog.paymentGateway = null
      state.redirectDialog.orderData = null
      state.redirectDialog.externalUrl = null
      state.redirectDialog.showDialog = false
      state.redirectDialog.title = ''
    },
    setPayPalDialogVisible: (state, action) => {
      state.payPalDialogVisible = action.payload
    },
    setCannotDeleteMessage: (state, action) => {
      state.cannotDeleteMessage.allowDelete = action.payload.allowDelete
    },
    setChatError: (state, action) => {
      const { showDialog, errorCode, errorMessage, errorTitle } = action.payload
      state.chatError.showDialog = showDialog
      state.chatError.errorCode = errorCode
      state.chatError.errorMessage = errorMessage
      state.chatError.errorTitle = errorTitle
    },
    setChangeMainVideo: (state, action) => {
      state.changeMainVideo = action.payload.changeMainVideo
    },
    setScrollPosition: (state, action) => {
      state.scrollPosition = action.payload
    },
    setMessageCount: (state, action) => {
      state.messageCount = action.payload
    },
    setStockError: (state, action) => {
      state.stockError = action.payload
    },
    setPostalCodeErrorMessage: (state, action) => {
      state.postalCodeErrorMessage = action.payload
    },
    setShowDraggable: (state, action) => {
      state.showDraggable = action.payload
    },
    setProductTourActive: (state, action) => {
      state.productTour.isActive = action.payload
    },
    setProductTourStep: (state, action) => {
      state.productTour.currentStep = action.payload
    },
    setProductTourCompleted: (state, action) => {
      state.productTour.completed = action.payload
    },
  },
})

//* ACTIONS
export const {
  setIsNotificationVisible,
  setNotificationMessage,
  setChatListIsShown,
  setRemoteLikeAnimation,
  setLocalLikeAnimation,
  setIsOneToMany,
  setLanguage,
  setAutoJoinChat,
  setVideoDimensions,
  setCurrenRowOnCarousel,
  showCheckoutIframe,
  hideCheckoutIframe,
  setActiveCallCard,
  closeCallCard,
  setActiveCallAction,
  showOnCloseMessageCall,
  hideOnCloseMessageCall,
  setAutoPlayUnlocker,
  setTencentConnectionDialog,
  setShowAskNameDialog,
  setPayPalDialogVisible,
  setRedirectDialog,
  resetRedirectDialog,
  setCannotDeleteMessage,
  setChangeMainVideo,
  setScrollPosition,
  setMessageCount,
  setStockError,
  setPostalCodeErrorMessage,
  setShowDraggable,
  setQuotedMessage,
  setChatCooldown,
  setDisabledByCooldown,
  clearQuotedMessage,
  setPinnedMessage,
  clearPinnedMessage,
  setPayPalButtonMounted,
  setMousePosition,
  setChatError,
  goToProducts,
  setExternalReaction,
  setAudioPermissionGranted,
  setQuickAdd,
  clearAutoPlayUnmuteRequest,
  clearTencentConnectionDialog,
  setShowTapToActivateSound,
  setProductTourActive,
  setProductTourStep,
  setProductTourCompleted,
  setIsChatRegistered,
} = uiSlice.actions

//* SELECTORS
export const selectNotificationMessage = (state) => state.ui.notificationMessage
export const selectIsNotificationVisible = (state) => state.ui.isNotificationVisible
export const selectQuotedMessage = (state) => state.ui.chat.quotedMessage
export const selectPinnedMessage = (state) => state.ui.chat.pinnedMessage
export const selectExternalReaction = (state) => state.ui.externalReaction
export const selectRemoteLikeVisible = (state) => state.ui.remoteLikeAnimation
export const selectLocalLikeVisible = (state) => state.ui.localLikeAnimation
export const selectIsLikeButtonVisible = (state) => state.ui.isLikeButtonVisible
export const selectActiveCallCard = (state) => state.ui.activeCallCard
export const selectActiveCallAction = (state) => state.ui.activeCallAction
export const selectVideoDimensions = (state) => state.ui.videoDimensions
export const selectCurrentRowOnCarousel = (state) => state.ui.carousel.currentRow
export const selectIsCheckoutIframeVisible = (state) => state.ui.cart.showCheckoutIframe
export const selectOnCloseMessageCall = (state) => state.ui.call.onCloseMessage
export const selectCartEnabled = (state) =>
  state.store.current.hasCheckout &&
  (state.store.current.isOneToManySale || state.call.role === CustomerRoles.BUYER)
export const selectPayPalButtonMounted = (state) => state.ui.payPalButtonMounted
export const selectAutoPlayUnlocker = (state) => state.ui.autoPlayUnlocker
export const selectTencentConnectionDialog = (state) => state.ui.tencentConnectionDialog
export const selectShowAskNameDialog = (state) => state.ui.showAskNameDialog.open
export const selectAllowCancelAskNameDialog = (state) => state.ui.showAskNameDialog.allowCancel
export const selectAskNameDialogTitle = (state) => state.ui.showAskNameDialog.title
export const selectAskNameDialogFromScheduled = (state) => state.ui.showAskNameDialog.fromScheduled
export const selectPayPalDialogVisible = (state) => state.ui.payPalDialogVisible
export const selectCannotDeleteMessage = (state) => state.ui.cannotDeleteMessage
export const selectRedirectDialog = (state) => state.ui.redirectDialog
export const selectMainVideo = (state) => state.ui.changeMainVideo
export const selectScrollPosition = (state) => state.ui.scrollPosition
export const selectMessages = (state) => state.ui.messages
export const selectAutoJoinChat = (state) => state.ui.chat.autoJoin
export const selectChatCooldown = (state) => state.ui.chat.cooldown
export const selectDisabledByCooldown = (state) => state.ui.chat.disabledByCooldown
export const selectIsStockError = (state) => state.ui.stockError
export const selectPostalCodeErrorMessage = (state) => state.ui.postalCodeErrorMessage
export const selectShowDraggable = (state) => state.ui.showDraggable
export const selectChatError = (state) => state.ui.chatError
export const selectStoreLang = (state) => state.ui.lang
export const selectQuickAdd = (state) => state.ui.quickAdd
export const selectProductTour = (state) => state.ui.productTour
export const selectAudioPermissionGranted = (state) => state.ui.audioPermissionGranted
export const selectIsChatRegistered = (state) => state.ui.isChatRegistered
//* REDUCER
export default uiSlice.reducer
